import { SendEmailAuthLinkError } from "@redotech/customer-portal-sdk/rpc/schema/send-email-auth-link";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { UserContext } from "@redotech/redo-customer-portal-app/contexts/user";
import { sendEmailAuthLinkErrorToText } from "@redotech/redo-customer-portal-app/login/dynamic-text-utils";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { Return } from "@redotech/redo-model/return";
import ClockRefresh from "@redotech/redo-web/arbiter-icon/clock-refresh_filled.svg";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ClientError } from "@redotech/rpc/errors";
import { memo, useContext, useState } from "react";
import { openUrl } from "./return-status";
import * as returnStatusCss from "./return-status/return-status.module.css";

export const ExchangeHeader = memo(function ExchangeHeader({
  returnData,
}: {
  returnData: Return;
}) {
  const isInstant = returnData.provision === "instant";
  return (
    <Flex align="center" dir="column" gap="lg" pb="xl">
      <Flex
        align="center"
        className={returnStatusCss.iconBackground}
        justify="center"
      >
        <ClockRefresh
          className={returnStatusCss.refreshIcon}
          height={28}
          width={28}
        />
      </Flex>

      <div className={returnStatusCss.exchangeTitle}>
        {" "}
        Your return submission is almost complete
      </div>
      <Text fontSize="md">
        Pay for your exchange to receive your return label and complete your
        submission.
      </Text>
      {!isInstant && (
        <Text fontSize="md">
          Your new order will be processed after your return items have been
          shipped back.
        </Text>
      )}
      <Button
        // @ts-expect-error: strict-null-checks
        onClick={() => openUrl(returnData.draftOrderURL)}
        radius="md"
        size={ButtonSize.MICRO}
        theme={ButtonTheme.PRIMARY}
      >
        Pay for exchange order
      </Button>
    </Flex>
  );
});

export const useShowAllOrdersButtonLoad = () => {
  const rpcClient = useContext(RpcClientContext);
  return useLoad(async () => {
    if (!rpcClient) return false;
    // Call with no arguments to avoid validation issues
    const shouldShowAllOrdersButton = await rpcClient.hasMultipleOrders({});
    return shouldShowAllOrdersButton;
  }, [rpcClient]);
};

export const useEmailAuth = () => {
  const rpcClient = useContext(RpcClientContext);
  const user = useContext(UserContext);
  const [emailSent, setEmailSent] = useState(false);
  const [showRetry, setShowRetry] = useState(false);

  const [sendEmailLoad, triggerSendEmail] = useTriggerLoad(async () => {
    if (!rpcClient || !user?.email) return;
    try {
      const widgetId = window.location.pathname.match(
        /^\/widget_id\/([^/]+)/,
      )?.[1];
      if (!widgetId) {
        console.error("No widget ID found in URL");
        return;
      }
      await rpcClient.sendEmailAuthLink({
        email: user.email,
        baseUrl: `${window.location.origin}/widget_id/${widgetId}`,
        nextUrl: "/returns-portal/orders",
      });
      setEmailSent(true);
      setTimeout(() => setShowRetry(true), 2000);
    } catch (error) {
      if (error instanceof ClientError) {
        const rpcError = error as ClientError<SendEmailAuthLinkError>;
        if (rpcError.code) {
          sendEmailLoad.error = sendEmailAuthLinkErrorToText[rpcError.code];
        }
      }
    }
  });

  return {
    emailSent,
    showRetry,
    triggerSendEmail,
    sendEmailError: sendEmailLoad.error?.message,
  };
};
